import { useReducer } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { doClearCreateOrder, doClearGetQuote, doClearGetQuoteChart } from 'src/actions';
import { OrderActionDto, OrderDurationDto, OrderTypeDto } from 'src/dtos';
import { SinglePageModal } from 'src/lib';
import { Quote } from 'src/models';
import { Maybe } from 'src/utils';

import { SecurityHoldingNewTradeSearchStep } from './SecurityHoldingNewTradeSearchStep';
import { SecurityHoldingTradeFailedStep } from './SecurityHoldingTradeFailedStep';
import { SecurityHoldingTradeMarketPriceStep } from './SecurityHoldingTradeMarketPriceStep';
import { SecurityHoldingTradeOrderFactorStep } from './SecurityHoldingTradeOrderFactorStep';
import { SecurityHoldingTradeOrderStep } from './SecurityHoldingTradeOrderStep';
import { SecurityHoldingTradeReviewStep } from './SecurityHoldingTradeReviewStep';
import { SecurityHoldingTradeServiceFeeStep } from './SecurityHoldingTradeServiceFeeStep';
import { SecurityHoldingTradeSucceededStep } from './SecurityHoldingTradeSucceededStep';

enum SecurityHoldingNewTradeStep {
  Search = 'Search',
  Details = 'Details',
  Order = 'Order',
  Review = 'Review',
  Succeeded = 'Succeeded',
  Failed = 'Failed',
  MarketPrice = 'MarketPrice',
  ServiceFee = 'ServiceFee',
  OrderFactors = 'OrderFactors',
}

export interface NewOrderReducerState {
  step: SecurityHoldingNewTradeStep;
  backStep?: SecurityHoldingNewTradeStep;
  duration?: OrderDurationDto;
  action?: OrderActionDto;
  type?: OrderTypeDto;
  price?: number;
  stop?: number;
  quantity?: number;
  symbol: string;
  description: string;
  estimatedTotal?: number;
}
const initialOrderReducerState: NewOrderReducerState = {
  step: SecurityHoldingNewTradeStep.Details,
  type: OrderTypeDto.Market,
  duration: OrderDurationDto.Day,
  action: OrderActionDto.Buy,
  symbol: '',
  description: '',
};

export type OrderReducerAction = { type: 'UPDATE'; payload: Partial<NewOrderReducerState> } | { type: 'RESET' };

const orderReducer = (state: NewOrderReducerState, action: OrderReducerAction) => {
  switch (action.type) {
    case 'UPDATE':
      return {
        ...state,
        ...action.payload,
      };
    case 'RESET':
      return initialOrderReducerState;
    default:
      return state;
  }
};

interface SecurityHoldingNewTradeModalProps {
  open: boolean;
  onClose: () => void;
}

export const SecurityHoldingNewTradeModal = ({ open, onClose }: SecurityHoldingNewTradeModalProps) => {
  const [orderState, orderDispatch] = useReducer(orderReducer, initialOrderReducerState);
  const dispatch = useDispatch();
  const quoteDetailsData: Maybe<Quote> = useSelector((state: any) => state.quotes.details.data);

  const _onClose = () => {
    onClose();
    orderDispatch({ type: 'RESET' });
    dispatch(doClearCreateOrder());
    dispatch(doClearGetQuote());
    dispatch(doClearGetQuoteChart());
  };

  const renderModal = () => {
    if (orderState.step === SecurityHoldingNewTradeStep.Order) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeOrderStep
            action={orderState.action}
            initialValues={orderState}
            quote={{
              id: orderState.symbol,
              symbol: orderState.symbol,
              description: orderState.description,
              change: quoteDetailsData?.change,
              last: quoteDetailsData?.last,
            }}
            onClose={_onClose}
            onBack={() =>
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingNewTradeStep.Details, backStep: undefined },
              })
            }
            onReview={values => {
              orderDispatch({
                type: 'UPDATE',
                payload: {
                  type: values.type,
                  duration: values.duration,
                  quantity: values.quantity ? Number(values.quantity) : undefined,
                  price: values.price ? Number(values.price) : undefined,
                  stop: values.stop ? Number(values.stop) : undefined,
                  estimatedTotal: values.estimatedTotal ? Number(values.estimatedTotal) : undefined,
                  step: SecurityHoldingNewTradeStep.Review,
                  backStep: SecurityHoldingNewTradeStep.Review,
                },
              });
            }}
            onLastPrice={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingNewTradeStep.MarketPrice, backStep: SecurityHoldingNewTradeStep.Order },
              });
            }}
            onServiceFee={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingNewTradeStep.ServiceFee, backStep: SecurityHoldingNewTradeStep.Order },
              });
            }}
          />
        ),
      };
    }

    if (orderState.step === SecurityHoldingNewTradeStep.Review) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeReviewStep
            quote={{
              id: orderState.symbol,
              symbol: orderState.symbol,
              description: orderState.description,
              change: quoteDetailsData?.change,
              last: quoteDetailsData?.last,
            }}
            order={orderState}
            onClose={_onClose}
            onBack={() =>
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingNewTradeStep.Order, backStep: SecurityHoldingNewTradeStep.Order },
              })
            }
            onLastPrice={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: {
                  step: SecurityHoldingNewTradeStep.MarketPrice,
                  backStep: SecurityHoldingNewTradeStep.Review,
                },
              });
            }}
            onServiceFee={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingNewTradeStep.ServiceFee, backStep: SecurityHoldingNewTradeStep.Review },
              });
            }}
            onReadMore={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: {
                  step: SecurityHoldingNewTradeStep.OrderFactors,
                  backStep: SecurityHoldingNewTradeStep.Review,
                },
              });
            }}
            onSuccess={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingNewTradeStep.Succeeded, backStep: undefined },
              });
            }}
            onError={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingNewTradeStep.Failed, backStep: undefined },
              });
            }}
          />
        ),
      };
    }

    if (orderState.step === SecurityHoldingNewTradeStep.MarketPrice) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeMarketPriceStep
            onBack={() => orderDispatch({ type: 'UPDATE', payload: { step: orderState.backStep } })}
          />
        ),
      };
    }

    if (orderState.step === SecurityHoldingNewTradeStep.ServiceFee) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeServiceFeeStep
            onBack={() => orderDispatch({ type: 'UPDATE', payload: { step: orderState.backStep } })}
          />
        ),
      };
    }

    if (orderState.step === SecurityHoldingNewTradeStep.OrderFactors) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeOrderFactorStep
            onBack={() => {
              orderDispatch({ type: 'UPDATE', payload: { step: orderState.backStep } });
            }}
          />
        ),
      };
    }

    if (orderState.step === SecurityHoldingNewTradeStep.Succeeded) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeSucceededStep
            quote={{
              id: orderState.symbol,
              symbol: orderState.symbol,
              description: orderState.description,
              change: quoteDetailsData?.change,
              last: quoteDetailsData?.last,
            }}
            onClose={_onClose}
            onLastPrice={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: {
                  step: SecurityHoldingNewTradeStep.MarketPrice,
                  backStep: SecurityHoldingNewTradeStep.Succeeded,
                },
              });
            }}
            onServiceFee={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: {
                  step: SecurityHoldingNewTradeStep.ServiceFee,
                  backStep: SecurityHoldingNewTradeStep.Succeeded,
                },
              });
            }}
          />
        ),
      };
    }

    if (orderState.step === SecurityHoldingNewTradeStep.Failed) {
      return {
        width: 600,
        onClose: _onClose,
        content: (
          <SecurityHoldingTradeFailedStep
            order={orderState}
            quote={{
              id: orderState.symbol,
              symbol: orderState.symbol,
              description: orderState.description,
              change: quoteDetailsData?.change,
              last: quoteDetailsData?.last,
            }}
            onClose={_onClose}
            onLastPrice={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: {
                  step: SecurityHoldingNewTradeStep.MarketPrice,
                  backStep: SecurityHoldingNewTradeStep.Failed,
                },
              });
            }}
            onServiceFee={() => {
              orderDispatch({
                type: 'UPDATE',
                payload: { step: SecurityHoldingNewTradeStep.ServiceFee, backStep: SecurityHoldingNewTradeStep.Failed },
              });
            }}
          />
        ),
      };
    }

    return {
      width: 600,
      onClose: _onClose,
      content: (
        <SecurityHoldingNewTradeSearchStep
          symbol={orderState.symbol}
          description={orderState.description}
          onClose={_onClose}
          onBuy={values => {
            orderDispatch({
              type: 'UPDATE',
              payload: {
                symbol: values.symbol,
                description: values.description,
                step: SecurityHoldingNewTradeStep.Order,
              },
            });
          }}
        />
      ),
    };
  };

  return <SinglePageModal open={open} {...renderModal()} />;
};
